/**
 * useAGUIChat Hook
 * Main hook for managing chat interactions with AG-UI
 */

import { useState, useCallback, useRef } from 'react';
import { useAGUIContext } from '../components/AGUIProvider';
import {
  RunAgentInput,
  BaseEvent,
  EventType,
  Message,
  AssistantMessage,
  UserMessage,
  TextMessageStartEvent,
  TextMessageContentEvent,
  ToolCallStartEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  AGUICustomEvent,
  RunErrorEvent,
  RunFinishedEvent,
} from '../types/agui';
import { UserMessageSchema, RunAgentInputSchema } from '@ag-ui/core';
import type { Subscription } from 'rxjs';
import { normalizeDGUIContent } from '../utils/normalizeDGUIContent';

export interface UseAGUIChatResult {
  // State
  messages: Message[]; // Use AG-UI Message type from library
  currentMessage: string | null;
  isStreaming: boolean;
  toolCalls: Map<string, ToolCallStatus>;
  error: Error | null;

  // Actions
  sendMessage: (message: string, threadId?: string) => Promise<void>;
  loadMessages: (messages: Message[]) => void;
  cancelStream: () => void;
  clearError: () => void;
}

export interface ToolCallStatus {
  tool: string;
  status: 'started' | 'finished' | 'error';
  result?: unknown;
  error?: string;
}

export function useAGUIChat(threadId?: string): UseAGUIChatResult {
  const { client } = useAGUIContext();
  const [messages, setMessages] = useState<Message[]>([]); // Use AG-UI Message type
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolCalls, setToolCalls] = useState<Map<string, ToolCallStatus>>(new Map());
  const [error, setError] = useState<Error | null>(null);
  
  const subscriptionRef = useRef<Subscription | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentThreadIdRef = useRef<string | undefined>(threadId);

  // Update thread ID ref when prop changes
  if (threadId !== currentThreadIdRef.current) {
    currentThreadIdRef.current = threadId;
  }

  const sendMessage = useCallback(
    async (message: string, providedThreadId?: string) => {
      const activeThreadId = providedThreadId || currentThreadIdRef.current;
      
      if (!activeThreadId) {
        throw new Error('Thread ID is required');
      }

      try {
        setIsStreaming(true);
        setError(null);
        setCurrentMessage('');

        // Add user message to local state (using AG-UI UserMessage type)
        // Validate against UserMessageSchema to ensure it matches AG-UI protocol
        const userMessageRaw = {
          id: `msg-${Date.now()}`,
          role: 'user' as const,
          content: message,
        };
        
        // Validate user message against AG-UI schema
        const userMessageValidation = UserMessageSchema.safeParse(userMessageRaw);
        if (!userMessageValidation.success) {
          throw new Error(`Invalid user message format: ${userMessageValidation.error.message}`);
        }
        const userMessage: UserMessage = userMessageValidation.data;
        
        // Update state (async, but we'll construct request with current + new message)
        setMessages((prev) => [...prev, userMessage]);

        // Prepare AG-UI request using RunAgentInput format (AG-UI protocol standard)
        // IMPORTANT: Use messages state + new userMessage to ensure the request includes all messages
        // Since setMessages is async, we need to manually include the new message in the request
        // Safeguard: Always ensure userMessage is included even if messages state is stale
        const requestMessages = messages.length > 0 ? [...messages, userMessage] : [userMessage];
        
        // RunAgentInput expects message.content to be string. Assistant messages may have
        // object content (e.g. { parts: [...] } from DGUI). Normalize so content is always string.
        const toContentString = (c: Message['content']): string =>
          typeof c === 'string' ? c : JSON.stringify(c);
        const messagesWithStringContent: Array<{ id: string; role: Message['role']; content: string }> =
          requestMessages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: toContentString(msg.content),
          }));
        
        // Verify userMessage is included
        if (!messagesWithStringContent.some(msg => msg.id === userMessage.id || msg.content === toContentString(userMessage.content))) {
          messagesWithStringContent.push({
            id: userMessage.id,
            role: userMessage.role,
            content: toContentString(userMessage.content),
          });
        }
        
        const runInputRaw: RunAgentInput = {
          threadId: activeThreadId,
          runId: `run-${Date.now()}`,
          messages: messagesWithStringContent as RunAgentInput['messages'],
          tools: [], // Frontend tools can be passed here
          context: [
            {
              description: 'UI State',
              value: JSON.stringify({
                current_page: typeof window !== 'undefined' ? window.location.pathname : '',
                // Can include selected instruments, etc.
              }),
            },
          ],
          state: {}, // Shared state (if any)
          forwardedProps: {}, // Required by AG-UI protocol schema
        };
        
        // Validate RunAgentInput against schema to ensure it matches AG-UI protocol
        const runInputValidation = RunAgentInputSchema.safeParse(runInputRaw);
        if (!runInputValidation.success) {
          throw new Error(`Invalid RunAgentInput format: ${runInputValidation.error.message}`);
        }
        const runInput: RunAgentInput = runInputValidation.data;

        // Create abort controller for cancellation
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Stream events using HttpAgent (returns Observable<BaseEvent>)
        let assistantMessageId: string | undefined;
        let assistantContent = '';

        const subscription = client
          .runAgent(runInput, abortController.signal)
          .subscribe({
            next: (event: BaseEvent) => {
              switch (event.type) {
                case EventType.TEXT_MESSAGE_START:
                  assistantMessageId = (event as TextMessageStartEvent).messageId;
                  break;

                case EventType.TEXT_MESSAGE_CONTENT:
                  const contentEvent = event as TextMessageContentEvent;
                  assistantContent += contentEvent.delta || '';
                  setCurrentMessage(assistantContent);
                  break;

                case EventType.TEXT_MESSAGE_END:
                  // Finalize assistant message (using AG-UI AssistantMessage type)
                  if (assistantMessageId && assistantContent) {
                    const normalized = normalizeDGUIContent(assistantContent);
                    const content =
                      normalized !== null ? normalized : assistantContent;
                    const assistantMessage: AssistantMessage = {
                      id: assistantMessageId,
                      role: 'assistant',
                      content: content as AssistantMessage['content'],
                    };
                    setMessages((prev) => [...prev, assistantMessage]);
                  }
                  setCurrentMessage(null);
                  assistantMessageId = undefined;
                  assistantContent = '';
                  break;

                case EventType.TOOL_CALL_START:
                  const toolStartEvent = event as ToolCallStartEvent;
                  setToolCalls((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(toolStartEvent.toolCallId, {
                      tool: toolStartEvent.toolCallName || 'unknown',
                      status: 'started',
                    });
                    return newMap;
                  });
                  break;

                case EventType.TOOL_CALL_END:
                  const toolEndEvent = event as ToolCallEndEvent;
                  setToolCalls((prev) => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(toolEndEvent.toolCallId);
                    if (existing) {
                      newMap.set(toolEndEvent.toolCallId, {
                        ...existing,
                        status: 'finished',
                      });
                    }
                    return newMap;
                  });
                  break;

                case EventType.TOOL_CALL_RESULT:
                  const toolResultEvent = event as ToolCallResultEvent;
                  setToolCalls((prev) => {
                    const newMap = new Map(prev);
                    const existing = newMap.get(toolResultEvent.toolCallId);
                    newMap.set(toolResultEvent.toolCallId, {
                      tool: existing?.tool || 'unknown',
                      status: 'finished',
                      result: toolResultEvent.content,
                    });
                    return newMap;
                  });
                  break;

                case EventType.STATE_DELTA:
                  // Handle state updates (handled by state sync utility)
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                      new globalThis.CustomEvent('agui-state-delta', {
                        detail: event,
                      })
                    );
                  }
                  break;

                case EventType.CUSTOM:
                  // Handle custom events (e.g., UI actions)
                  const customEvent = event as AGUICustomEvent;
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                      new globalThis.CustomEvent('agui-custom-event', {
                        detail: customEvent,
                      })
                    );
                  }
                  break;

                case EventType.RUN_FINISHED:
                  // Run completed successfully
                  // Extract session_id from RUN_FINISHED event if available
                  const finishedEvent = event as RunFinishedEvent;
                  if (finishedEvent.threadId && finishedEvent.threadId !== threadId && finishedEvent.threadId !== 'pending') {
                    // Update the thread ID ref for next message
                    currentThreadIdRef.current = finishedEvent.threadId;
                    // Dispatch custom event to notify parent component of session ID update
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(
                        new globalThis.CustomEvent('agui-session-updated', {
                          detail: { sessionId: finishedEvent.threadId },
                        })
                      );
                    }
                  }
                  break;

                case EventType.RUN_ERROR:
                  const errorEvent = event as RunErrorEvent;
                  setError(new Error(errorEvent.message || 'Unknown error'));
                  break;

                default:
                  // Handle other event types
                  break;
              }
            },
            error: (err) => {
              if (err.name !== 'AbortError') {
                setError(err instanceof Error ? err : new Error('Unknown error'));
              }
            },
            complete: () => {
              setIsStreaming(false);
              setCurrentMessage(null);
              subscriptionRef.current = null;
              abortControllerRef.current = null;
            },
          });

        // Store subscription for cancellation
        subscriptionRef.current = subscription;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setIsStreaming(false);
        setCurrentMessage(null);
      }
    },
    [client, messages]
  );

  const cancelStream = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setCurrentMessage(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadMessages = useCallback((newMessages: Message[]) => {
    setMessages(newMessages);
  }, []);

  return {
    messages,
    currentMessage,
    isStreaming,
    toolCalls,
    error,
    sendMessage,
    loadMessages,
    cancelStream,
    clearError,
  };
}
